'use client';

import { c as _c } from "react/compiler-runtime";
import { useMarketConfigCatalog, usePrefetchGetProposal, useSimilarMarketsByMarket } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type JSX, useEffect, useRef } from 'react';
import { ProposalFeedCard } from '@/components/launchpad/proposal-feed-card';
import { MarketRowCardView } from '@/components/markets/market-row-card';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { PROPOSAL_DETAIL_PREFETCH_FIELDS } from '@/lib/sdk/projections/launchpad';
import { adaptMarket, adaptProposal } from './adapt';
import { SimilarMarketsSectionSkeleton } from './similar-markets-section-skeleton';
const DEFAULT_LIMIT = 6;
const DISCOVERY_THRESHOLD = 0.45;
const VIEWPORT_PREFETCH_MARGIN = '400px';
export interface SimilarMarketsSectionProps {
  readonly marketPubkey: string;
}
export function SimilarMarketsSection(t0) {
  const $ = _c(39);
  const {
    marketPubkey
  } = t0;
  const t = useTranslations("markets.similar");
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const sectionRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: VIEWPORT_PREFETCH_MARGIN,
      once: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(sectionRef, t1);
  const target = isMdUp ? "_blank" : undefined;
  const rel = isMdUp ? "noopener noreferrer" : undefined;
  let t2;
  if ($[1] !== marketPubkey) {
    t2 = {
      pubkey: marketPubkey,
      limit: DEFAULT_LIMIT,
      threshold: DISCOVERY_THRESHOLD
    };
    $[1] = marketPubkey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isIntersecting) {
    t3 = {
      enabled: isIntersecting
    };
    $[3] = isIntersecting;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data,
    isLoading
  } = useSimilarMarketsByMarket(t2, t3);
  const {
    configs,
    ensure
  } = useMarketConfigCatalog();
  let t4;
  if ($[5] !== configs || $[6] !== data?.markets || $[7] !== data?.proposals || $[8] !== ensure) {
    t4 = () => {
      if (configs === undefined) {
        return;
      }
      for (const row of [...(data?.markets ?? []), ...(data?.proposals ?? [])]) {
        ensure(row.marketConfig);
      }
    };
    $[5] = configs;
    $[6] = data?.markets;
    $[7] = data?.proposals;
    $[8] = ensure;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== configs || $[11] !== data || $[12] !== ensure) {
    t5 = [data, configs, ensure];
    $[10] = configs;
    $[11] = data;
    $[12] = ensure;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  const prefetchProposal = usePrefetchGetProposal();
  let t6;
  if ($[14] !== prefetchProposal) {
    t6 = function onProposalHoverPrefetch(proposalPubkey) {
      prefetchProposal({
        pubkey: proposalPubkey,
        fields: PROPOSAL_DETAIL_PREFETCH_FIELDS
      });
    };
    $[14] = prefetchProposal;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onProposalHoverPrefetch = t6;
  if (!isIntersecting || isLoading) {
    let t7;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <section ref={sectionRef} data-testid="similar-markets-section" data-state="loading" className="flex flex-col gap-6"><SimilarMarketsSectionSkeleton /></section>;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  let t7;
  if ($[17] !== data?.markets) {
    t7 = data?.markets ?? [];
    $[17] = data?.markets;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const markets = t7;
  let t8;
  if ($[19] !== data?.proposals) {
    t8 = data?.proposals ?? [];
    $[19] = data?.proposals;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const proposals = t8;
  if (markets.length === 0 && proposals.length === 0) {
    return null;
  }
  const t9 = String(markets.length);
  const t10 = String(proposals.length);
  let t11;
  if ($[21] !== configs || $[22] !== markets || $[23] !== rel || $[24] !== t || $[25] !== target) {
    t11 = markets.length > 0 ? <div className="flex flex-col gap-3"><h3 className="text-heading-md text-foreground-primary">{t("similarMarkets")}</h3><div className="grid grid-cols-1 gap-3 md:grid-cols-2">{markets.map((m, i) => {
          const adapted = adaptMarket(m, configs?.[m.marketConfig]);
          return <MarketRowCardView identity={m} key={adapted.pubkey} pubkey={adapted.pubkey} title={adapted.title} coverImageUrl={adapted.coverImageUrl} coverImageNsfw={adapted.coverImageNsfw} location={adapted.location} category={adapted.category} volumeTotal={adapted.volumeTotal} prizePool={adapted.prizePool} outcomes={adapted.outcomes} position={i} activeSort="similarity" consensusThreshold={adapted.consensusThreshold} target={target} rel={rel} />;
        })}</div></div> : null;
    $[21] = configs;
    $[22] = markets;
    $[23] = rel;
    $[24] = t;
    $[25] = target;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== configs || $[28] !== onProposalHoverPrefetch || $[29] !== proposals || $[30] !== rel || $[31] !== t || $[32] !== target) {
    t12 = proposals.length > 0 ? <div className="flex flex-col gap-3"><h3 className="text-heading-md text-foreground-primary">{t("similarProposals")}</h3><div className="grid grid-cols-1 gap-3 md:grid-cols-2">{proposals.map(p => {
          const adapted_0 = adaptProposal(p, configs?.[p.marketConfig]);
          return <ProposalFeedCard key={adapted_0.proposal.pubkey} proposal={adapted_0.proposal} proposalScore={adapted_0.score} topBackers={adapted_0.topBackers} variant="compact" onHoverPrefetch={onProposalHoverPrefetch} target={target} rel={rel} />;
        })}</div></div> : null;
    $[27] = configs;
    $[28] = onProposalHoverPrefetch;
    $[29] = proposals;
    $[30] = rel;
    $[31] = t;
    $[32] = target;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t9) {
    t13 = <section ref={sectionRef} data-testid="similar-markets-section" data-state="rendered" data-markets={t9} data-proposals={t10} className="flex flex-col gap-6">{t11}{t12}</section>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t9;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}